import React, { useCallback, useMemo, useRef, useState } from 'react';
import { BrandMark, ZigzagStrip } from '../../Themes/Brand';

/**
 * The auth backdrop: a drawn orb web on night ink. Thin silk spokes, one
 * continuous capture spiral, dew catching light at a few intersections,
 * and the stabilimentum ribbon zigzagging through the hub — the product's
 * namesake, woven where a template would put gradient blobs.
 */

const HUB = { x: 700, y: 205 };
const SPOKES = 14;
const SILK = '#CFC7E4';
const RIBBON = '#8B77E8';

function webGeometry() {
    const spokeAngles = Array.from({ length: SPOKES }, (_, i) => (i * 2 * Math.PI) / SPOKES + 0.13);

    const spokes = spokeAngles.map(a => ({
        x1: HUB.x + Math.cos(a) * 14,
        y1: HUB.y + Math.sin(a) * 14,
        x2: HUB.x + Math.cos(a) * 520,
        y2: HUB.y + Math.sin(a) * 520,
    }));

    // One continuous capture spiral, slightly irregular like real silk.
    const turns = 4.6;
    const steps = Math.round(SPOKES * turns);
    const spiralPts: Array<{ x: number; y: number }> = [];
    for (let k = 0; k <= steps; k++) {
        const a = spokeAngles[k % SPOKES];
        const r = (36 + (k / steps) * 400) * (1 + 0.025 * Math.sin(k * 2.63));
        spiralPts.push({ x: HUB.x + Math.cos(a) * r, y: HUB.y + Math.sin(a) * r });
    }
    const spiral = spiralPts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(' ');

    // Stabilimentum: zigzag ribbon through the hub, long below, short above.
    const ribbonSeg = (from: number, to: number, dir: 1 | -1) => {
        const pts: string[] = [];
        let side = 1;
        for (let r = from; r <= to; r += 15) {
            const a = dir * (Math.PI / 2) + side * 0.10;
            pts.push(`${(HUB.x + Math.cos(a) * r).toFixed(1)} ${(HUB.y + Math.sin(a) * r).toFixed(1)}`);
            side = -side;
        }
        return 'M' + pts.join(' L');
    };
    const ribbon = `${ribbonSeg(20, 185, 1)} ${ribbonSeg(20, 95, -1)}`;

    // Dew drops on the spiral, spread over the lower half.
    const dew = [11, 19, 26, 34, 41, 52]
        .map(k => spiralPts[k])
        .filter(Boolean);

    return { spokes, spiral, ribbon, dew };
}

const OrbWeb: React.FC<{ mouse: { x: number; y: number } }> = ({ mouse }) => {
    const { spokes, spiral, ribbon, dew } = useMemo(webGeometry, []);

    return (
        <svg
            viewBox="0 0 1000 700"
            preserveAspectRatio="xMidYMid slice"
            aria-hidden="true"
            style={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                transform: `translate(${mouse.x * 16}px, ${mouse.y * 16}px) scale(1.06)`,
                transition: 'transform 0.12s ease-out',
                willChange: 'transform',
            }}
        >
            {spokes.map((s, i) => (
                <line key={i} x1={s.x1} y1={s.y1} x2={s.x2} y2={s.y2} stroke={SILK} strokeOpacity="0.10" strokeWidth="1" />
            ))}
            <path d={spiral} fill="none" stroke={SILK} strokeOpacity="0.14" strokeWidth="1" strokeLinejoin="round" />
            <circle cx={HUB.x} cy={HUB.y} r="3" fill={SILK} fillOpacity="0.22" />

            {/* soft glow under the ribbon, then the ribbon itself, drawn in on load */}
            <path d={ribbon} fill="none" stroke={RIBBON} strokeOpacity="0.12" strokeWidth="7" strokeLinejoin="round" strokeLinecap="round" />
            <path
                d={ribbon}
                fill="none"
                stroke={RIBBON}
                strokeOpacity="0.6"
                strokeWidth="2.1"
                strokeLinejoin="round"
                strokeLinecap="round"
                pathLength={1}
                className="st-ribbon"
            />

            {dew.map((p, i) => (
                <circle
                    key={i}
                    cx={p.x}
                    cy={p.y}
                    r="1.8"
                    fill="#EAE5F8"
                    className="st-dew"
                    style={{ animationDelay: `${i * 0.9}s` }}
                />
            ))}
        </svg>
    );
};

/** Full-viewport night scene; children are centered on top of the web. */
export const LoginScene: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [mouse, setMouse] = useState({ x: 0, y: 0 });
    const rafRef = useRef<number | null>(null);

    const handleMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
        const x = (e.clientX / window.innerWidth) - 0.5;
        const y = (e.clientY / window.innerHeight) - 0.5;
        if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
        rafRef.current = requestAnimationFrame(() => setMouse({ x, y }));
    }, []);

    return (
        <div
            style={{ position: 'fixed', inset: 0, backgroundColor: 'var(--st-ink)', overflow: 'hidden' }}
            onMouseMove={handleMouseMove}
        >
            <style>{`
                .st-dew { opacity: 0.12; }
                .st-ribbon { stroke-dasharray: 1; stroke-dashoffset: 0; }
                @media (prefers-reduced-motion: no-preference) {
                    .st-ribbon { animation: stRibbonDraw 1.4s ease-out; }
                    .st-dew { animation: stDewGlint 5.2s ease-in-out infinite; }
                }
                @keyframes stRibbonDraw {
                    from { stroke-dashoffset: 1; }
                    to   { stroke-dashoffset: 0; }
                }
                @keyframes stDewGlint {
                    0%, 100% { opacity: 0.12; }
                    50%      { opacity: 0.7; }
                }
                @keyframes loginSpin {
                    to { transform: rotate(360deg); }
                }
            `}</style>

            {/* moon-glow behind the hub */}
            <div style={{
                position: 'absolute',
                inset: 0,
                background: 'radial-gradient(circle at 70% 29%, rgba(109,87,212,0.16), transparent 42%)',
                pointerEvents: 'none',
            }} />

            <OrbWeb mouse={mouse} />

            <div style={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '1.5rem',
            }}>
                {children}
            </div>
        </div>
    );
};

/** The shared auth card: ink header with the wordmark, zigzag seam, paper body. */
export const AuthCard: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
    <div style={{
        width: '100%',
        maxWidth: '21.5rem',
        backgroundColor: 'var(--st-paper)',
        borderRadius: '16px',
        boxShadow: '0 32px 80px rgba(9, 7, 14, 0.6)',
        overflow: 'hidden',
    }}>
        <div style={{
            backgroundColor: 'var(--st-ink)',
            padding: '1.5rem 2rem 1.3rem',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '0.55rem',
        }}>
            <BrandMark fontSize="0.66rem" glyphColor="#8B77E8" color="rgba(243,241,247,0.55)" />
            <div style={{
                color: '#FFFFFF',
                fontFamily: 'var(--st-font-display)',
                fontSize: '1.1rem',
                fontWeight: 700,
                letterSpacing: '0.01em',
            }}>
                {title}
            </div>
        </div>
        <ZigzagStrip color="#6D57D4" height={7} />
        <div style={{ padding: '1.6rem 2rem 1.8rem' }}>
            {children}
        </div>
    </div>
);
